import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from '../../utils/AuthContext';
import { designSystem, commonStyles } from '../../styles/design-system';
import { 
  LogOutIcon, 
  SettingsIcon, 
  RefreshIcon, 
  DownloadIcon,
  EditIcon,
  TrashIcon,
  PlusIcon,
  SearchIcon,
  FilterIcon,
  ExternalLinkIcon,
  CheckCircleIcon
} from '../../utils/IconWrapper';
import BookingChart from '../../components/admin/analytics/BookingChart';
import AdvancedMetrics from '../../components/admin/analytics/AdvancedMetrics';
import BookingMap from '../../components/admin/analytics/BookingMap';
import RealTimeActivity from '../../components/admin/analytics/RealTimeActivity';
import UserManagement from '../../components/admin/users/UserManagement';
import BlacklistPanel from '../../components/admin/users/BlacklistPanel';
import VerificationPanel from '../../components/admin/users/VerificationPanel';
import ChannelSyncPanel from '../../components/admin/ChannelSyncPanel';
import AirbnbImportPanel from '../../components/admin/AirbnbImportPanel';
import ServiceRequestsPanel from '../../components/admin/ServiceRequestsPanel';
import AirbnbSyncSettings from '../../components/admin/AirbnbSyncSettings';
import { 
  generateSampleChartData, 
  generateSampleMetrics, 
  generateSampleLocationData 
} from '../../utils/realTimeUpdates';

// Styled Components con Design System J-O-H
const DashboardContainer = styled.div`
  min-height: 100vh;
  background: ${designSystem.colors.background.primary};
  padding: ${designSystem.spacing.xl};
  font-family: ${designSystem.typography.fontFamily.primary};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    padding: ${designSystem.spacing.md};
  }
`;

const Header = styled.div`
  ${commonStyles.card}
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${designSystem.spacing.xl};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${designSystem.spacing.md};
    text-align: center;
  }

  .header-content {
    h1 {
      ${commonStyles.gradientText}
      font-family: ${designSystem.typography.fontFamily.secondary};
      font-size: ${designSystem.typography.fontSize['4xl']};
      font-weight: ${designSystem.typography.fontWeight.bold};
      margin: 0 0 ${designSystem.spacing.sm} 0;
      
      @media (max-width: ${designSystem.breakpoints.tablet}) {
        font-size: ${designSystem.typography.fontSize['3xl']};
      }
    }

    p {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.lg};
      margin: 0;
    }
  }

  .header-actions {
    display: flex;
    gap: ${designSystem.spacing.sm};
    align-items: center;

    @media (max-width: ${designSystem.breakpoints.tablet}) {
      flex-wrap: wrap;
      justify-content: center;
    }
  }
`;

const TabsContainer = styled.div`
  ${commonStyles.card}
  margin-bottom: ${designSystem.spacing.xl};
  padding: ${designSystem.spacing.md};
  display: flex;
  flex-wrap: wrap;
  gap: ${designSystem.spacing.sm};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    justify-content: center;
  }
`;

const TabButton = styled.button<{ active: boolean }>`
  background: ${props => props.active 
    ? designSystem.colors.background.gradient
    : 'transparent'};
  color: ${props => props.active 
    ? designSystem.colors.text.white 
    : designSystem.colors.text.secondary};
  border: 2px solid ${props => props.active 
    ? 'transparent' 
    : designSystem.colors.border.light};
  padding: ${designSystem.spacing.md} ${designSystem.spacing.lg};
  border-radius: ${designSystem.borderRadius.lg};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  font-size: ${designSystem.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${designSystem.transitions.normal};
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.sm};
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.active 
      ? designSystem.colors.background.gradient
      : designSystem.colors.primary.main};
    color: ${designSystem.colors.text.white};
    transform: translateY(-2px);
    box-shadow: ${designSystem.shadows.hover};
    border-color: transparent;
  }

  .icon {
    font-size: ${designSystem.typography.fontSize.lg};
  }
`;

const StatsGrid = styled.div`
  ${commonStyles.gridResponsive}
  margin-bottom: ${designSystem.spacing.xl};
`;

const StatCard = styled(motion.div)`
  ${commonStyles.card}
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${designSystem.colors.background.gradient};
  }

  .icon {
    font-size: ${designSystem.typography.fontSize['5xl']};
    margin-bottom: ${designSystem.spacing.md};
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
  }

  .value {
    font-size: ${designSystem.typography.fontSize['4xl']};
    font-weight: ${designSystem.typography.fontWeight.bold};
    ${commonStyles.gradientText}
    margin-bottom: ${designSystem.spacing.sm};
    line-height: ${designSystem.typography.lineHeight.tight};
  }

  .label {
    color: ${designSystem.colors.text.secondary};
    font-weight: ${designSystem.typography.fontWeight.semibold};
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: ${designSystem.typography.fontSize.sm};
  }
`;

const ContentCard = styled.div`
  ${commonStyles.card}
  margin-bottom: ${designSystem.spacing.xl};

  h2 {
    ${commonStyles.titleSecondary}
    margin-bottom: ${designSystem.spacing.lg};
    padding-bottom: ${designSystem.spacing.md};
    border-bottom: 2px solid ${designSystem.colors.border.light};
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};

    .icon {
      font-size: ${designSystem.typography.fontSize['2xl']};
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${designSystem.spacing.lg};
  background: ${designSystem.colors.background.secondary};
  border-radius: ${designSystem.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${designSystem.shadows.sm};

  th, td {
    padding: ${designSystem.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${designSystem.colors.border.light};
    font-size: ${designSystem.typography.fontSize.sm};
    color: ${designSystem.colors.text.primary};
  }

  th {
    background: ${designSystem.colors.background.primary};
    font-weight: ${designSystem.typography.fontWeight.semibold};
    color: ${designSystem.colors.text.primary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: ${designSystem.typography.fontSize.xs};
  }

  tr:hover {
    background: ${designSystem.colors.background.tertiary};
  }

  tr:last-child td {
    border-bottom: none;
  }

  td strong {
    color: ${designSystem.colors.text.primary};
  }

  td small {
    color: ${designSystem.colors.text.secondary};
  }

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    font-size: ${designSystem.typography.fontSize.xs};
    
    th, td {
      padding: ${designSystem.spacing.sm};
    }
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: ${designSystem.colors.status.error};
          color: ${designSystem.colors.text.white};
          border: 2px solid ${designSystem.colors.status.error};
          
          &:hover {
            background: transparent;
            color: ${designSystem.colors.status.error};
          }
        `;
      case 'secondary':
        return commonStyles.buttonSecondary;
      default:
        return commonStyles.buttonPrimary;
    }
  }}
  
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.md};
  font-size: ${designSystem.typography.fontSize.xs};
  margin: 0 ${designSystem.spacing.xs};
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
    margin: ${designSystem.spacing.xs} 0;
    width: 100%;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.full};
  font-size: ${designSystem.typography.fontSize.xs};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  color: #000000;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  
  background: ${props => {
    switch (props.status) {
      case 'confirmed': return designSystem.colors.status.success;
      case 'pending': return designSystem.colors.status.warning;
      case 'cancelled': return designSystem.colors.status.error;
      default: return designSystem.colors.secondary.light;
    }
  }};
  
  ${props => props.status === 'cancelled' && `color: #ffffff;`}
`;

const AccommodationImage = styled.img`
  width: 60px;
  height: 45px;
  border-radius: ${designSystem.borderRadius.md};
  object-fit: cover;
  border: 2px solid ${designSystem.colors.border.light};
  transition: all ${designSystem.transitions.normal};
  
  &:hover {
    transform: scale(1.1);
    box-shadow: ${designSystem.shadows.hover};
  }
`;

const AccommodationActions = styled.div`
  display: flex;
  gap: ${designSystem.spacing.xs};
  align-items: center;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' | 'add' }>`
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.xs};
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.md};
  font-size: ${designSystem.typography.fontSize.xs};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  border: 1px solid;
  cursor: pointer;
  transition: all ${designSystem.transitions.normal};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.variant) {
      case 'edit':
        return `
          background: transparent;
          color: ${designSystem.colors.primary.main};
          border-color: ${designSystem.colors.primary.main};
          
          &:hover {
            background: ${designSystem.colors.primary.main};
            color: white;
          }
        `;
      case 'delete':
        return `
          background: transparent;
          color: ${designSystem.colors.status.error};
          border-color: ${designSystem.colors.status.error};
          
          &:hover {
            background: ${designSystem.colors.status.error};
            color: white;
          }
        `;
      case 'add':
        return `
          background: ${designSystem.colors.status.success};
          color: white;
          border-color: ${designSystem.colors.status.success};
          
          &:hover {
            background: transparent;
            color: ${designSystem.colors.status.success};
          }
        `;
      default:
        return commonStyles.buttonSecondary;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  ${commonStyles.card}
  ${commonStyles.flexCenter}
  padding: ${designSystem.spacing['5xl']};
  text-align: center;

  .spinner {
    ${commonStyles.loading}
    width: 40px;
    height: 40px;
    border: 4px solid ${designSystem.colors.border.light};
    border-top: 4px solid ${designSystem.colors.primary.main};
    border-radius: 50%;
    margin-bottom: ${designSystem.spacing.md};
  }

  .text {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.lg};
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: ${designSystem.spacing.xs};
  flex-wrap: wrap;

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const SearchFiltersContainer = styled.div`
  ${commonStyles.card}
  margin-bottom: ${designSystem.spacing.lg};
  padding: ${designSystem.spacing.lg};
  display: flex;
  gap: ${designSystem.spacing.md};
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;

  .icon {
    position: absolute;
    left: ${designSystem.spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${designSystem.colors.text.secondary};
  }

  input {
    ${commonStyles.input}
    padding-left: ${designSystem.spacing['3xl']};
  }
`;

const FilterSelect = styled.select`
  ${commonStyles.input}
  min-width: 150px;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
`;

const InfoBadge = styled.div`
  background: ${designSystem.colors.primary.main}20;
  color: ${designSystem.colors.primary.main};
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.md};
  border-radius: ${designSystem.borderRadius.lg};
  font-size: ${designSystem.typography.fontSize.sm};
  font-weight: ${designSystem.typography.fontWeight.medium};
  border: 1px solid ${designSystem.colors.primary.main}40;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${designSystem.zIndex.modal};
  padding: ${designSystem.spacing.lg};
`;

const ModalContent = styled(motion.div)`
  ${commonStyles.card}
  max-width: 500px;
  width: 100%;
  text-align: center;

  h3 {
    ${commonStyles.titleSecondary}
    margin-bottom: ${designSystem.spacing.lg};
    color: ${designSystem.colors.status.error};
  }

  .actions {
    display: flex;
    gap: ${designSystem.spacing.md};
    justify-content: center;
    margin-top: ${designSystem.spacing.xl};
  }
`;

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');
  const [chartData] = useState(generateSampleChartData());
  const [metricsData] = useState({
    ...generateSampleMetrics(),
    seasonalData: {
      labels: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu'],
      bookings: [45, 52, 38, 67, 89, 94],
      revenue: [12500, 15600, 11200, 19800, 25400, 28900]
    },
    bookingSourceData: {
      direct: 45,
      booking: 28,
      airbnb: 20,
      other: 7
    }
  });
  const [locationData] = useState(generateSampleLocationData());
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBookings: 0,
    totalAccommodations: 0,
    totalDocuments: 0,
    pendingBookings: 0,
    recentUsers: 0,
    monthlyRevenue: 0,
    verifiedUsers: 0
  });
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        // Carica statistiche di base
        const [usersResult, bookingsResult, accommodationsResult, documentsResult] = await Promise.all([
          supabase.from('user_profiles').select('id', { count: 'exact', head: true }),
          supabase.from('bookings').select('id', { count: 'exact', head: true }),
          supabase.from('accommodations').select('id', { count: 'exact', head: true }),
          supabase.from('uploaded_documents').select('id', { count: 'exact', head: true })
        ]);

        // Calcola statistiche aggiuntive
        const [pendingBookingsResult, recentUsersResult, verifiedUsersResult] = await Promise.all([
          supabase.from('bookings').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
          supabase.from('user_profiles').select('id', { count: 'exact', head: true }).gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),
          supabase.from('user_profiles').select('id', { count: 'exact', head: true }).eq('identity_verified', true)
        ]);

        // Calcola revenue (esempio)
        const { data: bookingsData } = await supabase
          .from('bookings')
          .select('total_price')
          .eq('status', 'confirmed')
          .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());
        
        const monthlyRevenue = bookingsData?.reduce((sum, booking) => sum + (booking.total_price || 0), 0) || 0;

        setStats({
          totalUsers: usersResult.count || 0,
          totalBookings: bookingsResult.count || 0,
          totalAccommodations: accommodationsResult.count || 0,
          totalDocuments: documentsResult.count || 0,
          pendingBookings: pendingBookingsResult.count || 0,
          recentUsers: recentUsersResult.count || 0,
          monthlyRevenue: monthlyRevenue,
          verifiedUsers: verifiedUsersResult.count || 0
        });
      } else if (activeTab === 'users') {
        const { data: users } = await supabase
          .from('user_profiles')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(20);
        setData(users || []);
      } else if (activeTab === 'bookings') {
        const { data: bookings } = await supabase
          .from('bookings')
          .select(`
            *,
            accommodation:accommodation_id(title_it, location_it)
          `)
          .order('created_at', { ascending: false })
          .limit(20);
        setData(bookings || []);
      } else if (activeTab === 'accommodations') {
        const { data: accommodations } = await supabase
          .from('accommodations')
          .select('*')
          .order('created_at', { ascending: false });
        setData(accommodations || []);
      } else if (activeTab === 'documents') {
        const { data: documents } = await supabase
          .from('uploaded_documents')
          .select(`
            *,
            user_profile:user_id(full_name, email, identity_verified)
          `)
          .order('created_at', { ascending: false })
          .limit(20);
        setData(documents || []);
      }
    } catch (error) {
      console.error('Errore caricamento dati:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserVerification = async (userId: string, verified: boolean) => {
    try {
      await supabase
        .from('user_profiles')
        .update({ identity_verified: verified })
        .eq('id', userId);
      loadData();
    } catch (error) {
      console.error('Errore aggiornamento:', error);
    }
  };

  const updateBookingStatus = async (bookingId: string, status: string) => {
    try {
      await supabase
        .from('bookings')
        .update({ status })
        .eq('id', bookingId);
      loadData();
    } catch (error) {
      console.error('Errore aggiornamento:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleExport = () => {
    const csvData = data.map(item => {
      if (activeTab === 'users') {
        return {
          Nome: item.full_name || 'N/A',
          Email: item.email || 'N/A',
          Telefono: item.phone_number || 'N/A',
          'Email Verificata': item.email_verified ? 'Sì' : 'No',
          'Telefono Verificato': item.phone_verified ? 'Sì' : 'No',
          'Identità Verificata': item.identity_verified ? 'Sì' : 'No',
          'Data Registrazione': new Date(item.created_at).toLocaleDateString('it-IT')
        };
      } else if (activeTab === 'bookings') {
        return {
          Ospite: item.guest_name,
          Email: item.guest_email,
          'Check-in': new Date(item.check_in).toLocaleDateString('it-IT'),
          'Check-out': new Date(item.check_out).toLocaleDateString('it-IT'),
          'Numero Ospiti': item.guests_count,
          'Totale (€)': item.total_price,
          Status: item.status
        };
      }
      return item;
    });
    
    const jsonData = JSON.stringify(csvData, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${activeTab}_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const deleteItem = async (id: string) => {
    try {
      let tableName = '';
      switch (activeTab) {
        case 'users':
          tableName = 'user_profiles';
          break;
        case 'bookings':
          tableName = 'bookings';
          break;
        case 'accommodations':
          tableName = 'accommodations';
          break;
        case 'documents':
          tableName = 'uploaded_documents';
          break;
        default:
          return;
      }
      
      await supabase.from(tableName).delete().eq('id', id);
      setShowDeleteModal(false);
      setSelectedItem(null);
      loadData();
    } catch (error) {
      console.error('Errore eliminazione:', error);
    }
  };

  // Funzioni specifiche per gestione alloggi
  const handleAddAccommodation = () => {
    navigate('/admin/accommodations/new');
  };

  const handleEditAccommodation = (accommodationId: string) => {
    navigate(`/admin/accommodations/edit/${accommodationId}`);
  };

  const handleDeleteAccommodation = (accommodation: any) => {
    setSelectedItem(accommodation);
    setShowDeleteModal(true);
  };

  const getAccommodationImageUrl = (imageUrl: string | null) => {
    if (!imageUrl) {
      return '/images/default-accommodation.jpg'; // Immagine di default
    }
    
    // Se è già una URL completa, restituiscila così com'è
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // Se è un path relativo, costruisci l'URL completo
    return imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
  };

  // Effetto per filtrare i dati
  useEffect(() => {
    let filtered = [...data];
    
    // Filtro per ricerca
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const searchStr = searchTerm.toLowerCase();
        if (activeTab === 'users') {
          return (item.full_name?.toLowerCase().includes(searchStr) ||
                  item.email?.toLowerCase().includes(searchStr) ||
                  item.phone_number?.includes(searchStr));
        } else if (activeTab === 'bookings') {
          return (item.guest_name?.toLowerCase().includes(searchStr) ||
                  item.guest_email?.toLowerCase().includes(searchStr));
        } else if (activeTab === 'accommodations') {
          return (item.title_it?.toLowerCase().includes(searchStr) ||
                  item.location_it?.toLowerCase().includes(searchStr));
        }
        return true;
      });
    }
    
    // Filtro per status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => {
        if (activeTab === 'users') {
          return filterStatus === 'verified' ? item.identity_verified : !item.identity_verified;
        } else if (activeTab === 'bookings') {
          return item.status === filterStatus;
        }
        return true;
      });
    }
    
    setFilteredData(filtered);
  }, [data, searchTerm, filterStatus, activeTab]);

  const tabs = [
    { id: 'overview', label: 'Panoramica', icon: '📊' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'realtime', label: 'Tempo Reale', icon: '⚡' },
    { id: 'sync', label: 'Sincronizzazione', icon: '🔄' },
    { id: 'airbnb-sync', label: 'Config Airbnb', icon: '🏠' },
    { id: 'airbnb', label: 'Import Airbnb', icon: '🏠' },
    { id: 'service-requests', label: 'Richieste Servizi', icon: '📝' },
    { id: 'users', label: 'Utenti', icon: '👥' },
    { id: 'user-management', label: 'Gestione Utenti', icon: '👥' },
    { id: 'blacklist', label: 'Blacklist/Whitelist', icon: '🛡️' },
    { id: 'verifications', label: 'Verifiche', icon: '✅' },
    { id: 'bookings', label: 'Prenotazioni', icon: '🏠' },
    { id: 'accommodations', label: 'Alloggi', icon: '🏛️' },
    { id: 'documents', label: 'Documenti', icon: '📄' }
  ];

  return (
    <DashboardContainer>
      <Header>
        <div className="header-content">
          <h1>🏛️ Pannello Amministrativo J-O-H</h1>
          <p>Gestione completa del sistema di prenotazioni storiche</p>
        </div>
        <div className="header-actions">
          <Button variant="secondary" onClick={handleRefresh}>
            <RefreshIcon size={16} />
            <span>Aggiorna</span>
          </Button>
          {activeTab !== 'overview' && (
            <Button variant="secondary" onClick={handleExport}>
              <DownloadIcon size={16} />
              <span>Esporta</span>
            </Button>
          )}
          <Button variant="secondary" onClick={() => navigate('/admin/settings')}>
            <SettingsIcon size={16} />
            <span>Impostazioni</span>
          </Button>
          <Button variant="danger" onClick={handleLogout}>
            <LogOutIcon size={16} />
            <span>Logout</span>
          </Button>
        </div>
      </Header>

      <TabsContainer>
        {tabs.map(tab => (
          <TabButton
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="icon">{tab.icon}</span>
            <span>{tab.label}</span>
          </TabButton>
        ))}
      </TabsContainer>

      {loading ? (
        <LoadingContainer>
          <div className="spinner"></div>
          <div className="text">Caricamento dati...</div>
        </LoadingContainer>
      ) : (
        <AnimatePresence mode="wait">
          {/* TAB PANORAMICA */}
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <StatsGrid>
                <StatCard whileHover={{ scale: 1.02, y: -4 }}>
                  <div className="icon">👥</div>
                  <div className="value">{stats.totalUsers}</div>
                  <div className="label">Utenti Registrati</div>
                </StatCard>

                <StatCard whileHover={{ scale: 1.02, y: -4 }}>
                  <div className="icon">✅</div>
                  <div className="value">{stats.verifiedUsers}</div>
                  <div className="label">Utenti Verificati</div>
                </StatCard>

                <StatCard whileHover={{ scale: 1.02, y: -4 }}>
                  <div className="icon">🏠</div>
                  <div className="value">{stats.totalBookings}</div>
                  <div className="label">Prenotazioni Totali</div>
                </StatCard>

                <StatCard whileHover={{ scale: 1.02, y: -4 }}>
                  <div className="icon">⏳</div>
                  <div className="value">{stats.pendingBookings}</div>
                  <div className="label">In Attesa Conferma</div>
                </StatCard>

                <StatCard whileHover={{ scale: 1.02, y: -4 }}>
                  <div className="icon">🏛️</div>
                  <div className="value">{stats.totalAccommodations}</div>
                  <div className="label">Alloggi Storici</div>
                </StatCard>

                <StatCard whileHover={{ scale: 1.02, y: -4 }}>
                  <div className="icon">💰</div>
                  <div className="value">€{stats.monthlyRevenue.toFixed(0)}</div>
                  <div className="label">Revenue Mensile</div>
                </StatCard>

                <StatCard whileHover={{ scale: 1.02, y: -4 }}>
                  <div className="icon">📄</div>
                  <div className="value">{stats.totalDocuments}</div>
                  <div className="label">Documenti Caricati</div>
                </StatCard>

                <StatCard whileHover={{ scale: 1.02, y: -4 }}>
                  <div className="icon">📈</div>
                  <div className="value">{stats.recentUsers}</div>
                  <div className="label">Nuovi Utenti (30g)</div>
                </StatCard>
              </StatsGrid>
            </motion.div>
          )}

          {/* TAB ANALYTICS */}
          {activeTab === 'analytics' && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <AdvancedMetrics {...metricsData} />
              
              <BookingChart 
                data={chartData} 
                chartType={chartType} 
                onChartTypeChange={setChartType} 
              />
              
              <BookingMap 
                locations={locationData} 
                totalBookings={stats.totalBookings} 
              />
            </motion.div>
          )}

          {/* TAB TEMPO REALE */}
          {activeTab === 'realtime' && (
            <motion.div
              key="realtime"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <RealTimeActivity />
            </motion.div>
          )}

          {/* TAB SINCRONIZZAZIONE */}
          {activeTab === 'sync' && (
            <motion.div
              key="sync"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ChannelSyncPanel />
            </motion.div>
          )}

          {/* TAB CONFIG AIRBNB */}
          {activeTab === 'airbnb-sync' && (
            <motion.div
              key="airbnb-sync"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <AirbnbSyncSettings />
            </motion.div>
          )}

          {/* TAB AIRBNB IMPORT */}
          {activeTab === 'airbnb' && (
            <motion.div
              key="airbnb"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <AirbnbImportPanel />
            </motion.div>
          )}

          {/* TAB RICHIESTE SERVIZI */}
          {activeTab === 'service-requests' && (
            <motion.div
              key="service-requests"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ServiceRequestsPanel />
            </motion.div>
          )}

          {/* TAB UTENTI */}
          {activeTab === 'users' && (
            <motion.div
              key="users"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <SearchFiltersContainer>
                <SearchInput>
                  <SearchIcon className="icon" size={18} />
                  <input
                    type="text"
                    placeholder="Cerca per nome, email o telefono..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </SearchInput>
                <FilterSelect
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">Tutti gli utenti</option>
                  <option value="verified">Solo verificati</option>
                  <option value="unverified">Non verificati</option>
                </FilterSelect>
                <InfoBadge>
                  Mostrando {filteredData.length} di {data.length} utenti
                </InfoBadge>
              </SearchFiltersContainer>

              <ContentCard>
                <h2>
                  <span className="icon">👥</span>
                  Gestione Utenti ({filteredData.length})
                </h2>
                
                <Table>
                  <thead>
                    <tr>
                      <th>Nome Completo</th>
                      <th>Email</th>
                      <th>Telefono</th>
                      <th>Status Verifiche</th>
                      <th>Data Registrazione</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map(user => (
                      <tr key={user.id}>
                        <td>
                          <strong>{user.full_name || 'N/A'}</strong>
                        </td>
                        <td>{user.email || 'N/A'}</td>
                        <td>{user.phone_number || 'N/A'}</td>
                        <td>
                          <div style={{ 
                            fontSize: designSystem.typography.fontSize.xs,
                            color: designSystem.colors.text.primary,
                            lineHeight: '1.4'
                          }}>
                            <span style={{ color: designSystem.colors.text.secondary }}>📧 Email:</span> {user.email_verified ? <span style={{ color: designSystem.colors.status.success }}>✅</span> : <span style={{ color: designSystem.colors.status.error }}>❌</span>}<br/>
                            <span style={{ color: designSystem.colors.text.secondary }}>📱 Telefono:</span> {user.phone_verified ? <span style={{ color: designSystem.colors.status.success }}>✅</span> : <span style={{ color: designSystem.colors.status.error }}>❌</span>}<br/>
                            <span style={{ color: designSystem.colors.text.secondary }}>🆔 Identità:</span> {user.identity_verified ? <span style={{ color: designSystem.colors.status.success }}>✅</span> : <span style={{ color: designSystem.colors.status.error }}>❌</span>}
                          </div>
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString('it-IT')}</td>
                                                  <td>
                            <ActionGroup>
                              {!user.identity_verified && (
                                <Button
                                  onClick={() => updateUserVerification(user.id, true)}
                                >
                                  ✅ Verifica
                                </Button>
                              )}
                              <Button
                                variant="danger"
                                onClick={() => {
                                  setSelectedItem(user);
                                  setShowDeleteModal(true);
                                }}
                              >
                                <TrashIcon size={14} />
                              </Button>
                            </ActionGroup>
                          </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ContentCard>
            </motion.div>
          )}

          {/* TAB GESTIONE UTENTI AVANZATA */}
          {activeTab === 'user-management' && (
            <motion.div
              key="user-management"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <UserManagement />
            </motion.div>
          )}

          {/* TAB BLACKLIST/WHITELIST */}
          {activeTab === 'blacklist' && (
            <motion.div
              key="blacklist"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <BlacklistPanel />
            </motion.div>
          )}

          {/* TAB VERIFICHE */}
          {activeTab === 'verifications' && (
            <motion.div
              key="verifications"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <VerificationPanel />
            </motion.div>
          )}
			
          {/* TAB PRENOTAZIONI */}
          {activeTab === 'bookings' && (
            <motion.div
              key="bookings"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <SearchFiltersContainer>
                <SearchInput>
                  <SearchIcon className="icon" size={18} />
                  <input
                    type="text"
                    placeholder="Cerca per nome ospite o email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </SearchInput>
                <FilterSelect
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">Tutte le prenotazioni</option>
                  <option value="pending">In attesa</option>
                  <option value="confirmed">Confermate</option>
                  <option value="cancelled">Annullate</option>
                </FilterSelect>
                <InfoBadge>
                  Mostrando {filteredData.length} di {data.length} prenotazioni
                </InfoBadge>
              </SearchFiltersContainer>

              <ContentCard>
                <h2>
                  <span className="icon">🏠</span>
                  Gestione Prenotazioni ({filteredData.length})
                </h2>
                
                <Table>
                  <thead>
                    <tr>
                      <th>Ospite</th>
                      <th>Alloggio</th>
                      <th>Periodo Soggiorno</th>
                      <th>Ospiti</th>
                      <th>Totale</th>
                      <th>Status</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map(booking => (
                      <tr key={booking.id}>
                        <td>
                          <strong>{booking.guest_name}</strong><br/>
                          <small style={{ color: designSystem.colors.text.secondary }}>
                            {booking.guest_email}
                          </small>
                        </td>
                        <td>{booking.accommodation?.title_it || 'N/A'}</td>
                        <td>
                          <div style={{ color: designSystem.colors.text.primary, lineHeight: '1.4' }}>
                            <strong style={{ color: designSystem.colors.text.secondary }}>Check-in:</strong> {new Date(booking.check_in).toLocaleDateString('it-IT')}<br/>
                            <strong style={{ color: designSystem.colors.text.secondary }}>Check-out:</strong> {new Date(booking.check_out).toLocaleDateString('it-IT')}
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <strong>{booking.guests_count}</strong>
                        </td>
                        <td>
                          <strong style={{ color: designSystem.colors.primary.main }}>
                            €{booking.total_price}
                          </strong>
                        </td>
                        <td>
                          <StatusBadge status={booking.status}>
                            {booking.status}
                          </StatusBadge>
                        </td>
                        <td>
                          <ActionGroup>
                            {booking.status === 'pending' && (
                              <>
                                <Button
                                  onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                >
                                  ✅ Conferma
                                </Button>
                                <Button
                                  variant="danger"
                                  onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                >
                                  ❌ Annulla
                                </Button>
                              </>
                            )}
                          </ActionGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ContentCard>
            </motion.div>
          )}

          {/* TAB ALLOGGI */}
          {activeTab === 'accommodations' && (
            <motion.div
              key="accommodations"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ContentCard>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>
                    <span className="icon">🏛️</span>
                    Gestione Alloggi Storici ({data.length})
                  </h2>
                  
                  <ActionButton 
                    variant="add" 
                    onClick={handleAddAccommodation}
                  >
                    <PlusIcon size={16} />
                    Nuovo Alloggio
                  </ActionButton>
                </div>
                
                <Table>
                  <thead>
                    <tr>
                      <th>Foto</th>
                      <th>Proprietà Storica</th>
                      <th>Località</th>
                      <th>Prezzo/Notte</th>
                      <th>Ospiti Max</th>
                      <th>Camere</th>
                      <th>Status</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map(accommodation => (
                      <tr key={accommodation.id}>
                        <td>
                          <AccommodationImage
                            src={getAccommodationImageUrl(accommodation.image)}
                            alt={accommodation.title_it}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/images/default-accommodation.jpg';
                            }}
                          />
                        </td>
                        <td>
                          <strong>{accommodation.title_it}</strong><br/>
                          <small style={{ color: designSystem.colors.text.secondary }}>
                            {accommodation.title_en}
                          </small>
                        </td>
                        <td>
                          <strong>{accommodation.location_it}</strong><br/>
                          <small style={{ color: designSystem.colors.text.secondary }}>
                            {accommodation.location_en}
                          </small>
                        </td>
                        <td>
                          <strong style={{ color: designSystem.colors.primary.main }}>
                            €{accommodation.price_per_night}
                          </strong>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <strong>{accommodation.max_guests}</strong>
                        </td>
                        <td style={{ textAlign: 'center', color: designSystem.colors.text.primary }}>
                          🛏️ {accommodation.bedrooms || 1} / 🚿 {accommodation.bathrooms || 1}
                        </td>
                        <td>
                          <StatusBadge status={accommodation.accommodation_status || 'active'}>
                            {accommodation.accommodation_status === 'active' ? 'Attivo' : 
                             accommodation.accommodation_status === 'inactive' ? 'Inattivo' : 
                             'Manutenzione'}
                          </StatusBadge>
                        </td>
                        <td>
                          <AccommodationActions>
                            <ActionButton 
                              variant="edit" 
                              onClick={() => handleEditAccommodation(accommodation.id)}
                              title="Modifica alloggio"
                            >
                              <EditIcon size={14} />
                              Modifica
                            </ActionButton>
                            
                            <ActionButton 
                              variant="delete" 
                              onClick={() => handleDeleteAccommodation(accommodation)}
                              title="Elimina alloggio"
                            >
                              <TrashIcon size={14} />
                              Elimina
                            </ActionButton>
                          </AccommodationActions>
                        </td>
                      </tr>
                    ))}
                    
                    {data.length === 0 && !loading && (
                      <tr>
                        <td colSpan={8} style={{ textAlign: 'center', padding: '3rem', color: designSystem.colors.text.secondary }}>
                          <div>
                            <h3 style={{ marginBottom: '1rem' }}>Nessun alloggio trovato</h3>
                            <p style={{ marginBottom: '1.5rem' }}>Inizia ad aggiungere i tuoi primi alloggi storici</p>
                            <ActionButton variant="add" onClick={handleAddAccommodation}>
                              <PlusIcon size={16} />
                              Aggiungi Primo Alloggio
                            </ActionButton>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </ContentCard>
            </motion.div>
          )}

          {/* TAB DOCUMENTI */}
          {activeTab === 'documents' && (
            <motion.div
              key="documents"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ContentCard>
                <h2>
                  <span className="icon">📄</span>
                  Documenti di Identità ({data.length})
                </h2>
                
                <Table>
                  <thead>
                    <tr>
                      <th>Utente</th>
                      <th>Tipo Documento</th>
                      <th>Data Upload</th>
                      <th>Status Verifica</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map(doc => (
                      <tr key={doc.id}>
                        <td>
                          <strong>{doc.user_profile?.full_name || 'N/A'}</strong><br/>
                          <small style={{ color: designSystem.colors.text.secondary }}>
                            {doc.user_profile?.email}
                          </small>
                        </td>
                        <td>
                          <strong>{doc.document_type}</strong>
                        </td>
                        <td>{new Date(doc.created_at).toLocaleDateString('it-IT')}</td>
                        <td>
                          {doc.user_profile?.identity_verified ? (
                            <StatusBadge status="confirmed">✅ Verificato</StatusBadge>
                          ) : (
                            <StatusBadge status="pending">⏳ Pendente</StatusBadge>
                          )}
                        </td>
                        <td>
                          <ActionGroup>
                            <Button
                              variant="secondary"
                              onClick={() => window.open(doc.file_url, '_blank')}
                            >
                              👁️ Visualizza
                            </Button>
                            {!doc.user_profile?.identity_verified && (
                              <Button
                                onClick={() => updateUserVerification(doc.user_id, true)}
                              >
                                ✅ Approva
                              </Button>
                            )}
                          </ActionGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ContentCard>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Modal di conferma eliminazione */}
      <AnimatePresence>
        {showDeleteModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteModal(false)}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>⚠️ Conferma Eliminazione</h3>
              <p>
                Sei sicuro di voler eliminare questo elemento? 
                <br />
                <strong>Questa azione non può essere annullata.</strong>
              </p>
              <div className="actions">
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Annulla
                </Button>
                <Button
                  variant="danger"
                  onClick={() => deleteItem(selectedItem?.id)}
                >
                  <TrashIcon size={16} />
                  Elimina Definitivamente
                </Button>
              </div>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </DashboardContainer>
  );
};

export default AdminDashboard; 